import React, { useState } from 'react';
import MediaQuery from 'react-responsive';
import { Box } from '@99designs/emu';
import { breakpoints } from '@99designs/emu/settings';
import { IconX1 } from '@99designs/icons';
import { FullscreenHeader } from '@99designs/spa-common';
import { FullscreenHeaderItem } from '@99designs/spa-common';
import { OnWhiteIconButton } from '@99designs/spa-common';
import { useFileOverlayContext } from '../../FileOverlay/FileOverlayContext';
import PaginationNavigation from '../../FileOverlay/Header/PaginationNavigation';
import ViewControls from '../../FileOverlay/Header/ViewControls/ViewControls';
import useIdleTimer from '../../FileOverlay/Header/useIdleTimer/useIdleTimer';
import useFileOverlayKeyboardShortcuts from '../../FileOverlay/useFileOverlayKeyboardShortcuts/useFileOverlayKeyboardShortcuts';

export interface KissHeaderProps {
    totalNumberOfFiles: number;
    currentPageNumber?: number;
    goBack: () => void;
    navNext?: () => void;
    navPrev?: () => void;
    isSidebarHidden: boolean;

    handleMaximize: () => void;
    handleMinimize: () => void;
    isPresentationMode: boolean;
}

function KissHeader({
    totalNumberOfFiles,
    currentPageNumber,
    goBack,
    navNext: navNextBase,
    navPrev: navPrevBase,
    isSidebarHidden,
    handleMaximize,
    handleMinimize,
    isPresentationMode,
}: KissHeaderProps) {
    const { viewSize, zoomIn, zoomOut, canZoomIn, canZoomOut } = useFileOverlayContext();
    const [userIsIdle, setIdle] = useState(false);

    const navNext = navNextBase
        ? () => {
              navNextBase();
          }
        : undefined;

    const navPrev = navPrevBase
        ? () => {
              navPrevBase();
          }
        : undefined;

    const navGoBack = () => {
        goBack();
    };

    useIdleTimer({ enabled: isPresentationMode, setIdle });
    useFileOverlayKeyboardShortcuts({
        isPresentationMode,
        zoomIn,
        zoomOut,
        canZoomIn,
        canZoomOut,
        handleMaximize,
        handleMinimize,
        goBack: navGoBack,
        navNext,
        navPrev,
    });

    return (
        <FullscreenHeader
            inactive={isPresentationMode && userIsIdle}
            designDetails
            data-testid="Header"
        >
            <FullscreenHeaderItem designDetails>
                <PaginationNavigation
                    totalNumberOfPages={totalNumberOfFiles}
                    currentPageNumber={currentPageNumber}
                    navNext={navNext}
                    navPrev={navPrev}
                />
            </FullscreenHeaderItem>
            <MediaQuery maxWidth={breakpoints.normal[0]}>
                {(isMobile) =>
                    isMobile ? (
                        <FullscreenHeaderItem designDetails>
                            <OnWhiteIconButton aria-label={__('Close')} onClick={navGoBack}>
                                <IconX1 />
                            </OnWhiteIconButton>
                        </FullscreenHeaderItem>
                    ) : (
                        <>
                            {viewSize.height && viewSize.width ? (
                                <FullscreenHeaderItem designDetails>
                                    <ViewControls
                                        handleMinimize={handleMinimize}
                                        handleMaximize={handleMaximize}
                                        isPresentationMode={isPresentationMode}
                                        zoomIn={zoomIn}
                                        zoomOut={zoomOut}
                                        canZoomIn={canZoomIn}
                                        canZoomOut={canZoomOut}
                                    />
                                </FullscreenHeaderItem>
                            ) : null}
                            {isSidebarHidden && !isPresentationMode && (
                                <FullscreenHeaderItem designDetails>
                                    <Box marginLeft="2bu">
                                        <OnWhiteIconButton
                                            aria-label={__('Close')}
                                            onClick={navGoBack}
                                        >
                                            <IconX1 />
                                        </OnWhiteIconButton>
                                    </Box>
                                </FullscreenHeaderItem>
                            )}
                        </>
                    )
                }
            </MediaQuery>
        </FullscreenHeader>
    );
}

export default KissHeader;
